import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  IconArrowRight,
  IconBell,
  IconCircleDot,
  IconFileDescription,
  IconFileText,
  IconHome,
  IconHomeShield,
  IconLayoutGrid,
  IconMap,
  IconMessageDots,
  IconPhoto,
  IconPresentation,
  IconShieldCheck,
  IconSparkles,
  IconTemplate,
  IconUpload,
  IconUserCircle,
  IconCrown,
  IconSearch,
  IconChevronRight,
  IconSquareDot,
  type TablerIcon,
} from "@tabler/icons-react";
import { useUserAuth } from "./auth";
import { userApi, type AppPublicConfigResponse } from "./api";
import { CHINA_AI_MODEL_OPTIONS, type ChinaAiModel } from "./ai-model-options";
import { createFileHandoff } from "./file-handoff";
import { isSupportedImageFile, isSupportedSourceFile, sourceFileType, SOURCE_FILE_ACCEPT, SOURCE_FILE_LABEL } from "./source-files";
import { Badge, Brand, ButtonLink } from "./ui";
import { StylePreview } from "./style-selector";
import { MARKETING_STYLES } from "./marketing-styles";
import { validatePptxHomeHandoff } from "./marketing-navigation";
import {
  DEFAULT_PPT_AUDIENCE,
  PPT_AUDIENCE_AUTO_VALUE,
  PPT_AUDIENCE_CUSTOM_VALUE,
  PPT_AUDIENCE_OPTIONS,
  pptAudienceSelectValue,
  type PptSelectionMode,
} from "./ppt-audience-options";
import {
  DEFAULT_PPT_SCENE,
  PPT_SCENE_AUTO_VALUE,
  PPT_SCENE_CUSTOM_VALUE,
  PPT_SCENE_OPTIONS,
  pptSceneSelectValue,
} from "./ppt-scene-options";
import {
  FILE_SOURCE_TEXT_MAX_LENGTH,
  FILE_SOURCE_TEXT_MIN_LENGTH,
  validateFileSourceTextLength,
} from "./ppt-text-limits";

export function MarketingHeader() {
  const auth = useUserAuth();
  const authenticated = auth.state === "authenticated";
  return <header className="marketing-header"><Link to="/"><Brand /></Link><nav><Link to="/styles">风格案例</Link><Link to="/pricing">价格</Link><Link to="/help">帮助</Link></nav><div><ButtonLink variant="ghost" to={authenticated ? "/app" : "/app/login"}>{authenticated ? auth.me?.user.nickname || "工作台" : "登录"}</ButtonLink><ButtonLink to={authenticated ? "/app/new" : "/app/login"} state={authenticated ? undefined : { redirectTo: "/app/new" }}>{authenticated ? "新建 PPT" : "免费体验"}</ButtonLink></div></header>;
}

export function LandingPage() {
  const auth = useUserAuth();
  const generationUploadLimitMb = auth.me?.uploadLimits?.maxTotalMb ?? 100;
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState("");
  const [appConfig, setAppConfig] = useState<AppPublicConfigResponse>();
  const [aiModel, setAiModel] = useState<ChinaAiModel>("deepseek");
  const [audienceMode, setAudienceMode] = useState<PptSelectionMode>("auto");
  const [audience, setAudience] = useState<string>(DEFAULT_PPT_AUDIENCE);
  const [sceneMode, setSceneMode] = useState<PptSelectionMode>("auto");
  const [scene, setScene] = useState<string>(DEFAULT_PPT_SCENE);
  const [customScene, setCustomScene] = useState("");
  const [generationMode, setGenerationMode] = useState<"standard" | "image">("standard");
  const [theme, setTheme] = useState("theme01");
  const [pageCount, setPageCount] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [homeFiles, setHomeFiles] = useState<File[]>([]);
  const [homeFileBusy, setHomeFileBusy] = useState(false);
  const [homeStyleCategory, setHomeStyleCategory] = useState<TemplateCategoryKey>("all");
  const homeFileInput = useRef<HTMLInputElement | null>(null);
  const filteredHomeStyles = MARKETING_STYLES.filter((style) => homeStyleCategory === "all" || style.category === homeStyleCategory);
  const edition = auth.me?.features?.edition || appConfig?.edition.edition;
  const isChinaEdition = edition === "china";
  const visibleGenerationMethods = isChinaEdition
    ? generationMethods.filter((method) => method.edition !== "global-only")
    : generationMethods;

  useEffect(() => {
    let cancelled = false;
    userApi<AppPublicConfigResponse>("/api/app/config")
      .then((value) => { if (!cancelled) setAppConfig(value); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  const openNewPpt = async (preset: NewPptPreset = {}) => {
    if (auth.state === "loading") return;
    setUploadError("");
    const source = preset.source || "file";
    const pptxHandoffError = source === "pptx" ? validatePptxHomeHandoff(homeFiles) : undefined;
    if (pptxHandoffError) {
      setUploadError(pptxHandoffError);
      return;
    }
    setHomeFileBusy(true);
    try {
      const fileHandoff = homeFiles.length ? await createFileHandoff(homeFiles) : undefined;
      const destination = newPptPath({
        source,
        text: prompt,
        title: "",
        ...(isChinaEdition ? { aiModel } : {}),
        audienceMode,
        audience,
        sceneMode,
        scene,
        ...(sceneMode === "custom" ? { customScene } : {}),
        generation: generationMode,
        theme,
        pages: pageCount,
        ...(fileHandoff ? { fileHandoff } : {}),
        ...preset,
      });
      if (auth.state === "authenticated") {
        navigate(destination);
        return;
      }
      navigate("/app/login", { state: { redirectTo: destination } });
    } catch (caught) {
      setUploadError(caught instanceof Error ? caught.message : "资料暂存失败，请重新选择后再试。");
    } finally {
      setHomeFileBusy(false);
    }
  };
  const generateFromHome = () => {
    const textError = validateFileSourceTextLength(prompt);
    if (textError) {
      setUploadError(textError);
      return;
    }
    if (audienceMode === "custom" && !audience.trim()) {
      setUploadError("请填写自定义目标受众。");
      return;
    }
    if (sceneMode === "custom" && !customScene.trim()) {
      setUploadError("请填写自定义使用场景。");
      return;
    }
    void openNewPpt();
  };
  const uploadHomeFiles = (selected: FileList | null) => {
    setUploadError("");
    if (!selected?.length) return;
    if (auth.state === "loading") {
      setUploadError("登录状态加载中，请稍后再上传。");
      return;
    }
    const incoming = Array.from(selected);
    const next = [...homeFiles, ...incoming];
    if (next.length > 5) setUploadError("官网最多添加 5 个资料文件。");
    else if (incoming.find((file) => !isSupportedSourceFile(file))) setUploadError(`请选择支持的资料文件：${SOURCE_FILE_LABEL}`);
    else if (incoming.find((file) => isSupportedImageFile(file) && file.size > 10 * 1024 * 1024)) setUploadError("单张图片不能超过 10 MB。");
    else if (next.reduce((total, file) => total + file.size, 0) > generationUploadLimitMb * 1024 * 1024) setUploadError(`所有资料总大小不能超过 ${generationUploadLimitMb} MB。`);
    else setHomeFiles(next);
    if (homeFileInput.current) homeFileInput.current.value = "";
  };
  const removeHomeFile = (index: number) => setHomeFiles((current) => current.filter((_, currentIndex) => currentIndex !== index));
  const useHomeStyle = (styleCode: string) => {
    void openNewPpt({ source: "file", generation: "image", theme: styleCode });
  };

  return (
    <OfficialShell active="home">
        <section className="official-hero" aria-label="AI PPT 介绍">
          <div className="official-hero-copy">
            <h1>会表达，<br />就会做 <span>PPT</span></h1>
            <p>AI理解你的想法，自动生成高质量演示文稿</p>
            <div className="official-value-row">
              <span><IconCircleDot />AI智能生成</span>
              <span><IconSquareDot />专业设计模板</span>
              <span><IconCircleDot />内容精准匹配</span>
            </div>
          </div>
          <OfficialHeroBanner />
        </section>

        <section className="official-composer" aria-label="输入 PPT 需求">
          <label htmlFor="official-prompt">你想做什么PPT?</label>
          <div className="official-prompt-box">
            <textarea
              id="official-prompt"
              value={prompt}
              minLength={FILE_SOURCE_TEXT_MIN_LENGTH}
              maxLength={FILE_SOURCE_TEXT_MAX_LENGTH}
              onChange={(event) => setPrompt(event.target.value)}
              placeholder="例如：制作一份科技类产品年度市场分析报告"
            />
            <span>{prompt.length} / {FILE_SOURCE_TEXT_MAX_LENGTH.toLocaleString()}</span>
          </div>
          <div className="official-composer-actions">
            <div className="official-tool-stack">
              <div className="official-tool-row official-tool-primary-row">
                <button type="button" disabled={auth.state === "loading" || homeFileBusy} onClick={() => homeFileInput.current?.click()}><IconUpload size={19} stroke={1.9} />{homeFileBusy ? "准备中" : "上传资料"}</button>
                <input ref={homeFileInput} type="file" accept={SOURCE_FILE_ACCEPT} multiple hidden onChange={(event) => uploadHomeFiles(event.target.files)} />
                {!isChinaEdition && <button type="button" disabled={homeFileBusy} onClick={() => void openNewPpt({ source: "pptx" })}><IconPresentation size={19} stroke={1.9} />美化 PPT</button>}
                {isChinaEdition && <label className="official-tool-select official-tool-ai-model"><span>AI模型</span><select aria-label="选择 AI 模型" value={aiModel} onChange={(event) => setAiModel(event.target.value as ChinaAiModel)}>{CHINA_AI_MODEL_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>}
                <label className="official-tool-select official-tool-audience"><span>目标受众</span><select aria-label="选择 PPT 目标受众" value={pptAudienceSelectValue(audienceMode, audience)} onChange={(event) => { const value = event.target.value; if (value === PPT_AUDIENCE_CUSTOM_VALUE) { setAudienceMode("custom"); setAudience(""); } else if (value === PPT_AUDIENCE_AUTO_VALUE) { setAudienceMode("auto"); setAudience(""); } else { setAudienceMode("preset"); setAudience(value); } }}>{PPT_AUDIENCE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
                {audienceMode === "custom" && <label className="official-tool-select official-tool-custom"><span>自定义受众</span><input aria-label="填写自定义 PPT 目标受众" value={audience} onChange={(event) => setAudience(event.target.value)} maxLength={1_000} placeholder="例如：市文旅局领导" autoFocus/></label>}
                <label className="official-tool-select"><span>场景</span><select aria-label="选择 PPT 场景" value={pptSceneSelectValue(sceneMode, scene)} onChange={(event) => { const value = event.target.value; if (value === PPT_SCENE_CUSTOM_VALUE) { setSceneMode("custom"); setScene(DEFAULT_PPT_SCENE); setCustomScene(""); } else if (value === PPT_SCENE_AUTO_VALUE) { setSceneMode("auto"); setScene(DEFAULT_PPT_SCENE); setCustomScene(""); } else { setSceneMode("preset"); setScene(value); setCustomScene(""); } }}>{PPT_SCENE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
                {sceneMode === "custom" && <label className="official-tool-select official-tool-custom"><span>自定义场景</span><input aria-label="填写自定义 PPT 使用场景" value={customScene} onChange={(event) => setCustomScene(event.target.value)} maxLength={1_000} placeholder="例如：项目试点汇报" autoFocus={audienceMode !== "custom"}/></label>}
                <label className="official-tool-select"><span>风格</span><select aria-label="选择 PPT 风格" value={`${generationMode}:${theme}`} onChange={(event) => { const [mode, nextTheme] = event.target.value.split(":"); setGenerationMode(mode === "image" ? "image" : "standard"); setTheme(nextTheme || "theme01"); }}><optgroup label="标准模式">{HOME_STYLE_OPTIONS.filter((option) => option.mode === "standard").map((option) => <option key={option.key} value={`${option.mode}:${option.key}`}>{option.name}</option>)}</optgroup><optgroup label="图片模式">{HOME_STYLE_OPTIONS.filter((option) => option.mode === "image").map((option) => <option key={option.key} value={`${option.mode}:${option.key}`}>{option.name}</option>)}</optgroup></select></label>
                <label className="official-tool-select"><span>页数</span><select aria-label="选择 PPT 页数" value={pageCount} onChange={(event) => setPageCount(event.target.value)}><option value="8">8 页</option><option value="12">12 页</option><option value="16">16 页</option><option value="20">20 页</option><option value="">智能推荐</option></select></label>
              </div>
              {homeFiles.length > 0 && <div className="official-file-list" aria-label="已选择的资料文件" aria-live="polite">
                <div className="official-file-list-head"><strong>已选资料 ({homeFiles.length}/5)</strong><span>将带入新建 PPT 页面</span></div>
                <ul>{homeFiles.map((file, index) => <li key={`${file.name}-${file.lastModified}-${index}`}>
                  <span className="official-file-type">{sourceFileType(file.name)?.label || "文件"}</span>
                  <span className="official-file-name" title={file.name}>{file.name}</span>
                  <small>{formatFileSize(file.size)}</small>
                  <button type="button" disabled={homeFileBusy} onClick={() => removeHomeFile(index)} aria-label={`删除 ${file.name}`}>删除</button>
                </li>)}</ul>
              </div>}
            </div>
            <button className="official-generate-button" type="button" disabled={auth.state === "loading" || homeFileBusy} onClick={generateFromHome}>
              <IconSparkles size={23} fill="currentColor" stroke={1.7} />
              AI生成PPT
            </button>
          </div>
          {uploadError && <div className="form-error">{uploadError}</div>}
        </section>

        <section className="official-methods" aria-label="PPT 制作方式">
          <h2><span>多种方式，</span>统一进入生成平台确认</h2>
          <div className="official-method-grid">
            {visibleGenerationMethods.map((method) => <MethodCard key={method.title} method={method} onClick={() => void openNewPpt(method.preset)} />)}
          </div>
        </section>

        <section className="official-style-showcase" aria-labelledby="official-style-title">
          <div className="official-style-heading">
            <h2 id="official-style-title">选择你的专业设计风格</h2>
            <Link to="/styles">更多模板 <IconChevronRight size={18} stroke={2.3} /></Link>
          </div>
          <div className="official-style-categories" aria-label="设计风格分类">
            {TEMPLATE_CATEGORIES.slice(0, 6).map((category) => (
              <button
                className={category.key === homeStyleCategory ? "active" : ""}
                type="button"
                key={category.key}
                aria-pressed={category.key === homeStyleCategory}
                onClick={() => setHomeStyleCategory(category.key)}
              >
                {category.label}
              </button>
            ))}
          </div>
          <div className="official-style-grid">
            {filteredHomeStyles.map((style) => (
              <button type="button" className="template-card template-card-button official-style-card" key={style.code} onClick={() => useHomeStyle(style.code)}>
                <StylePreview style={style} />
                <div className="official-style-card-foot">
                  <span>12 页</span>
                  <strong>立即使用</strong>
                </div>
              </button>
            ))}
          </div>
        </section>

        <section className="official-tutorial" aria-label="PPT 生成教程">
          <div className="official-tutorial-steps">
            <h2>3步生成专业PPT</h2>
            <ol>
              <li>
                <span aria-hidden="true">01</span>
                <div><h3>输入需求</h3><p>输入主题或上传资料，描述需<br />强调重点，AI理解内容</p></div>
              </li>
              <li>
                <span aria-hidden="true">02</span>
                <div><h3>AI生成</h3><p>AI自动生成文稿和内容，匹配<br />设计风格，生成完整PPT</p></div>
              </li>
              <li>
                <span aria-hidden="true">03</span>
                <div><h3>在线编辑优化</h3><p>在线编辑调整内容，自动排版<br />保存到下载与分享</p></div>
              </li>
            </ol>
          </div>
          <OfficialTutorialBanner />
        </section>
    </OfficialShell>
  );
}

const OFFICIAL_BANNER_SLIDES = [
  { src: "/assets/landing/banner/banner01.png", alt: "企业知识库增长复盘 PPT 示例" },
  { src: "/assets/landing/banner/banner02.png", alt: "试点价值规模复制 PPT 示例" },
  { src: "/assets/landing/banner/banner03.png", alt: "风险治理与复盘机制 PPT 示例" },
  { src: "/assets/landing/banner/banner04.png", alt: "城市音乐节商业化活动策划 PPT 示例" },
  { src: "/assets/landing/banner/banner05.png", alt: "投资决策汇报 PPT 示例" },
] as const;

const OFFICIAL_TUTORIAL_SLIDES = [
  { src: "/assets/landing/tutorial/tutorial-banner-home.png", alt: "幻视 PPT 官网首页" },
  { src: "/assets/landing/tutorial/tutorial-banner-create.png", alt: "新建 PPT 页面" },
  { src: "/assets/landing/tutorial/tutorial-banner-result.png", alt: "PPT 项目结果页面" },
] as const;

function OfficialTutorialBanner() {
  const [activeSlide, setActiveSlide] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const timer = window.setInterval(() => {
      setActiveSlide((current) => (current + 1) % OFFICIAL_TUTORIAL_SLIDES.length);
    }, 4_000);
    return () => window.clearInterval(timer);
  }, [paused]);

  return (
    <div
      className="official-tutorial-banner"
      aria-roledescription="carousel"
      aria-label="幻视 PPT 使用流程展示"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setPaused(false);
      }}
    >
      <div className="official-tutorial-stage">
        <div className="official-tutorial-track" style={{ transform: `translateX(-${activeSlide * 100}%)` }}>
          {OFFICIAL_TUTORIAL_SLIDES.map((slide, index) => (
            <img key={slide.src} src={slide.src} alt={slide.alt} aria-hidden={index !== activeSlide} loading={index === 0 ? "eager" : "lazy"} />
          ))}
        </div>
      </div>
      <div className="official-tutorial-dots" role="tablist" aria-label="选择流程展示图片">
        {OFFICIAL_TUTORIAL_SLIDES.map((slide, index) => (
          <button key={slide.src} type="button" className={index === activeSlide ? "active" : ""} role="tab" aria-selected={index === activeSlide} aria-label={`查看第 ${index + 1} 张流程展示`} onClick={() => setActiveSlide(index)} />
        ))}
      </div>
    </div>
  );
}

function OfficialHeroBanner() {
  const [activeSlide, setActiveSlide] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const timer = window.setInterval(() => {
      setActiveSlide((current) => (current + 1) % OFFICIAL_BANNER_SLIDES.length);
    }, 4_500);
    return () => window.clearInterval(timer);
  }, [paused]);

  return (
    <section
      className="official-hero-banner"
      aria-label="AI PPT 案例轮播"
      aria-roledescription="carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setPaused(false);
      }}
    >
      <div className="official-banner-stage">
        <div className="official-banner-track" style={{ transform: `translateX(-${activeSlide * 100}%)` }}>
          {OFFICIAL_BANNER_SLIDES.map((slide, index) => (
            <img
              key={slide.src}
              src={slide.src}
              alt={slide.alt}
              aria-hidden={index !== activeSlide}
              loading={index === 0 ? "eager" : "lazy"}
            />
          ))}
        </div>
      </div>
      <div className="official-banner-thumbnails" role="tablist" aria-label="选择 PPT 案例">
        {OFFICIAL_BANNER_SLIDES.map((slide, index) => (
          <button
            key={slide.src}
            type="button"
            className={index === activeSlide ? "active" : ""}
            role="tab"
            aria-selected={index === activeSlide}
            aria-label={`查看案例 ${index + 1}`}
            onClick={() => setActiveSlide(index)}
          >
            <img src={slide.src} alt="" loading="lazy" />
          </button>
        ))}
      </div>
      <div className="official-banner-dots" aria-hidden="true">
        {OFFICIAL_BANNER_SLIDES.map((slide, index) => (
          <i key={slide.src} className={index === activeSlide ? "active" : ""} />
        ))}
      </div>
    </section>
  );
}

type OfficialNavKey = "home" | "styles" | "enterprise" | "pricing" | "help";

const OFFICIAL_NAV_ORDER: OfficialNavKey[] = ["home", "styles", "enterprise", "pricing"];
let previousOfficialNavIndex: number | null = null;

function OfficialShell({ active, children }: { active: OfficialNavKey; children: ReactNode }) {
  const auth = useUserAuth();
  const navigate = useNavigate();
  const [appConfig, setAppConfig] = useState<AppPublicConfigResponse>();
  const activeNavIndex = OFFICIAL_NAV_ORDER.indexOf(active);
  const previousNavIndex = previousOfficialNavIndex ?? activeNavIndex;
  const isChinaEdition = (auth.me?.features?.edition || appConfig?.edition.edition) === "china";

  useEffect(() => {
    if (activeNavIndex >= 0) previousOfficialNavIndex = activeNavIndex;
  }, [activeNavIndex]);

  useEffect(() => {
    let cancelled = false;
    userApi<AppPublicConfigResponse>("/api/app/config")
      .then((value) => { if (!cancelled) setAppConfig(value); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  const navStyle = {
    "--official-nav-active-y": `${Math.max(activeNavIndex, 0) * 78}px`,
    "--official-nav-previous-y": `${Math.max(previousNavIndex, 0) * 78}px`,
  } as CSSProperties;

  return (
    <div className="official-home">
      <a className="skip-link" href="#official-main">跳到主要内容</a>
      <aside className="official-sidebar" aria-label="官网导航">
        <Link to="/" className="official-logo" aria-label="幻视PPT 首页">
          <img src="/assets/brand/huanshi-ppt-logo.png" alt="" />
          <strong>幻视PPT</strong>
        </Link>
        <nav className="official-nav" style={navStyle}>
          {activeNavIndex >= 0 ? <span key={active} className="official-nav-selection" aria-hidden="true" /> : null}
          <OfficialNavItem to="/" icon={IconHome} active={active === "home"}>首页</OfficialNavItem>
          <OfficialNavItem to="/styles" icon={IconLayoutGrid} active={active === "styles"}>模板库</OfficialNavItem>
          <OfficialNavItem to="/enterprise" icon={IconHomeShield} active={active === "enterprise"}>企业版</OfficialNavItem>
          <OfficialNavItem to="/pricing" icon={IconShieldCheck} active={active === "pricing"}>价格</OfficialNavItem>
        </nav>
      </aside>

      <main className="official-main" id="official-main" tabIndex={-1}>
        <div className="official-topbar" aria-label="账户操作">
          <button className="official-icon-button" type="button" aria-label="消息通知"><IconBell size={21} stroke={1.8} /></button>
          <button className="official-icon-button" type="button" aria-label="会员权益" onClick={() => navigate("/pricing")}><IconCrown size={22} stroke={1.8} /></button>
          {auth.state === "authenticated" ? (
            <Link
              className="official-login-button is-account"
              to="/app"
              aria-label={`${auth.me?.user.nickname || "用户"}，进入用户工作台`}
            >
              <span className="official-account-avatar"><IconUserCircle size={25} stroke={1.8} aria-hidden="true" /></span>
              <span className="official-account-name">{auth.me?.user.nickname || "用户"}</span>
            </Link>
          ) : (
            <button className="official-login-button" type="button" disabled={auth.state === "loading"} aria-label="登录" onClick={() => navigate("/app/login")}>{auth.state === "loading" ? "…" : "登录"}</button>
          )}
        </div>
        {children}
        {isChinaEdition && <nav className="official-legal-links" aria-label="法律与合规"><a href="https://beian.miit.gov.cn/#/" target="_blank" rel="noopener noreferrer">苏ICP备20038330号-16</a><a href="/legal/privacy-policy.html" target="_blank" rel="noopener noreferrer">隐私政策</a><a href="/legal/user-agreement.html" target="_blank" rel="noopener noreferrer">用户协议</a></nav>}
      </main>
    </div>
  );
}

function OfficialNavItem({ to, icon: Icon, active = false, children }: { to: string; icon: TablerIcon; active?: boolean; children: ReactNode }) {
  return <Link to={to} className={active ? "active" : ""} aria-current={active ? "page" : undefined}><Icon size={23} stroke={1.85} /><span>{children}</span></Link>;
}

interface GenerationMethod {
  title: string;
  description: string;
  icon: TablerIcon;
  accent: string;
  preset: NewPptPreset;
  edition?: "global-only";
}

function MethodCard({ method, onClick }: { method: GenerationMethod; onClick: () => void }) {
  const Icon = method.icon;
  return (
    <button type="button" className="official-method-card" onClick={onClick}>
      <span className="official-method-icon" style={{ "--method-accent": method.accent } as CSSProperties}>
        <Icon size={31} stroke={2} />
      </span>
      <div>
        <h3>{method.title}</h3>
        <p>{method.description}</p>
      </div>
      <IconArrowRight size={20} stroke={1.9} />
    </button>
  );
}

export function StylesPage() {
  const auth = useUserAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<TemplateCategoryKey>("all");
  const [sort, setSort] = useState<"latest" | "popular">("latest");
  const availableCategories = TEMPLATE_CATEGORIES.filter((item) => item.key === "all" || MARKETING_STYLES.some((style) => style.category === item.key));
  const normalizedQuery = query.trim().toLocaleLowerCase("zh-CN");
  const filteredStyles = MARKETING_STYLES
    .filter((style) => category === "all" || style.category === category)
    .filter((style) => !normalizedQuery || `${style.name} ${style.tagline} ${style.category}`.toLocaleLowerCase("zh-CN").includes(normalizedQuery));
  const visibleStyles = sort === "latest"
    ? filteredStyles
    : [...filteredStyles].sort((left, right) => STYLE_POPULARITY.indexOf(left.code) - STYLE_POPULARITY.indexOf(right.code));
  const useStyle = (styleCode: string) => {
    const destination = newPptPath({ source: "file", generation: "image", theme: styleCode });
    if (auth.state === "authenticated") navigate(destination);
    else if (auth.state !== "loading") navigate("/app/login", { state: { redirectTo: destination } });
  };
  return (
    <OfficialShell active="styles">
      <section className="official-subpage official-template-page">
        <h1>模板库</h1>
        <label className="template-search">
          <span className="sr-only">搜索模板</span>
          <input type="search" placeholder="搜索模板主题、行业或关键词" value={query} onChange={(event) => setQuery(event.target.value)} />
          <IconSearch size={22} stroke={2.1} />
        </label>
        <div className="template-toolbar">
          <div className="template-category-row" aria-label="模板分类">
            {availableCategories.map((item) => <button className={category === item.key ? "active" : ""} type="button" key={item.key} aria-pressed={category === item.key} onClick={() => setCategory(item.key)}>{item.label}</button>)}
          </div>
          <div className="template-sort-row" aria-label="模板排序">
            <div className="template-sort-toggle" role="group" aria-label="模板排序切换">
              <button className={sort === "latest" ? "active" : ""} type="button" aria-pressed={sort === "latest"} onClick={() => setSort("latest")}>最新</button>
              <button className={sort === "popular" ? "active" : ""} type="button" aria-pressed={sort === "popular"} onClick={() => setSort("popular")}>最热</button>
            </div>
            <span className="template-result-count" aria-live="polite">{visibleStyles.length} 个结果</span>
          </div>
        </div>
        {visibleStyles.length > 0 ? <div className="style-showcase template-card-grid">
          {visibleStyles.map((style) => (
            <button type="button" className="style-panel template-card template-card-button" key={style.code} onClick={() => useStyle(style.code)}>
              <StylePreview style={style} />
              <div className="template-card-foot">
                <div>
                  <h3>{style.name}</h3>
                  <p>{style.tagline}</p>
                </div>
                <span className="template-use-action">使用此风格</span>
              </div>
            </button>
          ))}
        </div> : <div className="template-empty" role="status"><strong>没有找到匹配模板</strong><p>试试缩短关键词，或切换到“全部”分类。</p><button type="button" onClick={() => { setQuery(""); setCategory("all"); }}>清除筛选</button></div>}
      </section>
    </OfficialShell>
  );
}

export function PricingPage() {
  const auth = useUserAuth();
  const navigate = useNavigate();
  const [billing, setBilling] = useState<"monthly" | "yearly">("yearly");
  const [highlightedComparisonColumn, setHighlightedComparisonColumn] = useState<number | null>(null);
  const continueWithPlan = (planKey: string) => {
    if (planKey === "enterprise") { navigate("/enterprise"); return; }
    const destination = planKey === "free" ? "/app/new" : "/app/account#membership";
    if (auth.state === "authenticated") navigate(destination);
    else if (auth.state !== "loading") navigate("/app/login", { state: { redirectTo: destination } });
  };

  return (
    <OfficialShell active="pricing">
      <section className="official-subpage official-pricing-page">
        <header className="pricing-sticky-head">
          <h1>价格</h1>
          <p>选择适合您的会员方案，解锁更多 AI 能力，让 PPT 创作更高效。</p>
        </header>

        <div className="pricing-billing-toggle" aria-label="计费周期">
          <button type="button" className={billing === "monthly" ? "active" : ""} aria-pressed={billing === "monthly"} onClick={() => setBilling("monthly")}>月付</button>
          <button type="button" className={billing === "yearly" ? "active" : ""} aria-pressed={billing === "yearly"} onClick={() => setBilling("yearly")}>年付</button>
          <span>立省 20%</span>
        </div>

        <section className="pricing-plan-grid" aria-label="会员方案">
          {pricingPlans.map((plan) => (
            <article key={plan.key} className={`pricing-plan-card ${plan.recommended ? "selected" : ""}`}>
              {plan.recommended && <span className="pricing-recommend">推荐</span>}
              <span className="pricing-plan-title"><PricingPlanIcon kind={plan.icon} /><strong>{plan.name}</strong></span>
              <span className="pricing-price">{plan.price}<small>{plan.suffix}</small></span>
              <span className="pricing-subprice">{billing === "yearly" ? plan.yearlyNote : plan.monthlyNote}</span>
              <span className="pricing-description">{plan.description}</span>
              <button type="button" className={`pricing-action ${plan.recommended ? "primary" : ""}`} onClick={() => continueWithPlan(plan.key)}>{plan.action}</button>
              <span className="pricing-feature-list">
                {plan.features.map((feature) => <span key={feature}><PricingCheck />{feature}</span>)}
              </span>
            </article>
          ))}
        </section>

        <section className="pricing-comparison" onMouseLeave={() => setHighlightedComparisonColumn(null)}>
          <h2>版本功能对比</h2>
          <table>
            <thead>
              <tr>{["功能 / 权益", "免费版", "个人会员", "团队会员", "企业定制"].map((head, index) => (
                <th
                  key={head}
                  className={index > 0 && highlightedComparisonColumn === index - 1 ? "comparison-column-active" : ""}
                  onMouseEnter={index > 0 ? () => setHighlightedComparisonColumn(index - 1) : undefined}
                  onClick={index > 0 ? () => setHighlightedComparisonColumn(index - 1) : undefined}
                >
                  {head}
                </th>
              ))}</tr>
            </thead>
            <tbody>
              {pricingComparison.map((row) => <tr key={row.label}><th>{row.label}</th>{row.values.map((value, index) => (
                <td
                  key={`${row.label}-${index}`}
                  className={highlightedComparisonColumn === index ? "comparison-column-active" : ""}
                  onMouseEnter={() => setHighlightedComparisonColumn(index)}
                  onClick={() => setHighlightedComparisonColumn(index)}
                >
                  {value === "check" ? <PricingCheck /> : value}
                </td>
              ))}</tr>)}
            </tbody>
          </table>
        </section>

        <section className="pricing-faq-section">
          <h2>常见问题</h2>
          <div className="pricing-faq-grid">
            {pricingFaqs.map((item) => (
              <article key={item.title}>
                <PricingFaqIcon kind={item.icon} />
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </article>
            ))}
          </div>
        </section>

        <footer className="pricing-security">
          <div><PricingFaqIcon kind="shield" /><span>安全可靠</span><i /> <span>数据加密</span><i /> <span>隐私保护</span></div>
          <p>所有套餐均符合企业级安全标准，保障您的数据安全与隐私。</p>
        </footer>
      </section>
    </OfficialShell>
  );
}

export function EnterprisePage() {
  const auth = useUserAuth();
  return (
    <OfficialShell active="enterprise">
      <section className="official-subpage enterprise-page">
        <section className="enterprise-hero">
          <div><span className="eyebrow">企业版</span><h1>让团队用同一套标准，<br/>持续产出专业演示</h1><p>围绕品牌模板、团队权限与交付流程进行方案评估；部署、集成与服务范围以实际需求确认为准。</p><div className="enterprise-actions"><ButtonLink to={auth.state === "authenticated" ? "/app/account#membership" : "/app/login"} state={auth.state === "authenticated" ? undefined : { redirectTo: "/app/account#membership" }}>申请方案评估</ButtonLink><ButtonLink variant="secondary" to="/pricing">查看价格对比</ButtonLink></div></div>
          <div className="enterprise-proof" aria-label="企业方案重点"><span>01</span><strong>品牌一致</strong><p>统一模板、视觉语言与交付规范。</p><span>02</span><strong>协作可控</strong><p>根据团队规模评估成员与权限方案。</p><span>03</span><strong>落地清晰</strong><p>从试用评估到部署培训，明确实施路径。</p></div>
        </section>
        <section className="enterprise-capabilities" aria-label="企业能力"><article><IconTemplate size={28} /><h2>品牌与模板</h2><p>沉淀专属模板和常用场景，让不同成员输出保持一致。</p></article><article><IconShieldCheck size={28} /><h2>安全与治理</h2><p>按组织要求评估权限、数据边界与部署方式。</p></article><article><IconSparkles size={28} /><h2>服务与落地</h2><p>结合团队工作流制定试用、培训和持续优化计划。</p></article></section>
        <section className="enterprise-steps"><div><span className="eyebrow">合作流程</span><h2>先验证价值，再决定投入</h2></div><ol><li><b>1</b><span><strong>需求访谈</strong>明确团队、场景与合规边界</span></li><li><b>2</b><span><strong>小范围试用</strong>用真实材料验证质量与效率</span></li><li><b>3</b><span><strong>方案落地</strong>确认配置、培训和支持范围</span></li></ol></section>
      </section>
    </OfficialShell>
  );
}

export function HelpPage() {
  return (
    <OfficialShell active="help">
      <section className="official-subpage">
        <section className="subpage-hero">
          <span className="eyebrow">帮助中心</span>
          <h1>从资料到 PPT，只需四步</h1>
          <p>上传资料、确认大纲、选择风格、生成下载。正式短信、支付和客服渠道将在上线阶段接入。</p>
        </section>
        <section className="section subpage-content">
          <div className="faq-list">
            {["支持哪些文件？", "为什么要先确认大纲？", "生成后还能修改吗？", "PPTX 是否可以继续编辑？"].map((q, i) => <details key={q} open={i === 0}><summary>{q}</summary><p>{[`当前生成平台支持文字和 ${SOURCE_FILE_LABEL} 资料。PPTX 可作为普通资料参与重新规划，也可从“美化现有 PPT”入口保留逐页内容并重新设计。`, "大纲决定整套演示的逻辑。先确认内容，可以减少昂贵且无效的重复生成。", "支持单页或整套 AI revision，具体次数取决于生成档位。", "专业设计档位的元素可编辑性更强；不同生成方式的能力会在选择时明确说明。"][i]}</p></details>)}
          </div>
        </section>
      </section>
    </OfficialShell>
  );
}

type PricingPlanIconKind = "gift" | "user" | "team" | "building";
type PricingFaqIconKind = "refresh" | "document" | "people" | "shield";

function PricingPlanIcon({ kind }: { kind: PricingPlanIconKind }) {
  return (
    <span className="pricing-svg-icon" aria-hidden="true">
      <svg viewBox="0 0 48 48" focusable="false">
        <defs>
          <linearGradient id={`pricing-icon-${kind}`} x1="10" y1="8" x2="38" y2="40" gradientUnits="userSpaceOnUse">
            <stop stopColor="#2e31ff" />
            <stop offset="1" stopColor="#8e20ff" />
          </linearGradient>
        </defs>
        {kind === "gift" && <>
          <path d="M12 21h24v18H12z" fill="none" stroke={`url(#pricing-icon-${kind})`} strokeWidth="4" strokeLinejoin="round" />
          <path d="M9 16h30v8H9zM24 16v23" fill="none" stroke={`url(#pricing-icon-${kind})`} strokeWidth="4" strokeLinejoin="round" />
          <path d="M18.6 16c-5.2-2.6-3.3-8.7 1.6-6.6 2.1.9 3.8 3.2 3.8 6.6M29.4 16c5.2-2.6 3.3-8.7-1.6-6.6-2.1.9-3.8 3.2-3.8 6.6" fill="none" stroke={`url(#pricing-icon-${kind})`} strokeWidth="4" strokeLinecap="round" />
        </>}
        {kind === "user" && <>
          <circle cx="24" cy="17" r="7" fill="none" stroke={`url(#pricing-icon-${kind})`} strokeWidth="4" />
          <path d="M11 39c1.8-8 7-12 13-12s11.2 4 13 12" fill="none" stroke={`url(#pricing-icon-${kind})`} strokeWidth="4" strokeLinecap="round" />
        </>}
        {kind === "team" && <>
          <circle cx="19" cy="17" r="6" fill={`url(#pricing-icon-${kind})`} />
          <circle cx="31" cy="18" r="5" fill={`url(#pricing-icon-${kind})`} opacity=".78" />
          <path d="M8 38c1.6-7.2 6.1-10.8 11-10.8S28.4 30.8 30 38" fill="none" stroke={`url(#pricing-icon-${kind})`} strokeWidth="4" strokeLinecap="round" />
          <path d="M27 29c4.8.5 8.7 3.7 10 9" fill="none" stroke={`url(#pricing-icon-${kind})`} strokeWidth="4" strokeLinecap="round" opacity=".78" />
        </>}
        {kind === "building" && <>
          <path d="M13 39V12h13v27M26 22h10v17M9 39h30" fill="none" stroke={`url(#pricing-icon-${kind})`} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M18 18h3M18 25h3M18 32h3M31 28h2M31 34h2" fill="none" stroke={`url(#pricing-icon-${kind})`} strokeWidth="3" strokeLinecap="round" />
        </>}
      </svg>
    </span>
  );
}

function PricingFaqIcon({ kind }: { kind: PricingFaqIconKind }) {
  return (
    <span className="pricing-svg-icon pricing-faq-icon" aria-hidden="true">
      <svg viewBox="0 0 48 48" focusable="false">
        <defs>
          <linearGradient id={`pricing-faq-${kind}`} x1="10" y1="8" x2="38" y2="40" gradientUnits="userSpaceOnUse">
            <stop stopColor="#2e31ff" />
            <stop offset="1" stopColor="#8e20ff" />
          </linearGradient>
        </defs>
        {kind === "refresh" && <>
          <path d="M35 17a13 13 0 0 0-22 4" fill="none" stroke={`url(#pricing-faq-${kind})`} strokeWidth="4" strokeLinecap="round" />
          <path d="M35 10v9h-9M13 31a13 13 0 0 0 22-4" fill="none" stroke={`url(#pricing-faq-${kind})`} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M13 38v-9h9" fill="none" stroke={`url(#pricing-faq-${kind})`} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
        </>}
        {kind === "document" && <>
          <path d="M15 9h14l7 7v23H15z" fill="none" stroke={`url(#pricing-faq-${kind})`} strokeWidth="4" strokeLinejoin="round" />
          <path d="M29 9v8h7M21 25h9M21 32h11" fill="none" stroke={`url(#pricing-faq-${kind})`} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
        </>}
        {kind === "people" && <>
          <circle cx="20" cy="18" r="5.5" fill="none" stroke={`url(#pricing-faq-${kind})`} strokeWidth="4" />
          <circle cx="32" cy="20" r="4.2" fill="none" stroke={`url(#pricing-faq-${kind})`} strokeWidth="3.5" opacity=".82" />
          <path d="M10 38c1.5-7.3 5.6-11 10-11s8.5 3.7 10 11M29 29c4.2.7 7.1 3.7 8 9" fill="none" stroke={`url(#pricing-faq-${kind})`} strokeWidth="4" strokeLinecap="round" />
        </>}
        {kind === "shield" && <>
          <path d="M24 8l14 5v10c0 9-5.6 14.2-14 18-8.4-3.8-14-9-14-18V13z" fill="none" stroke={`url(#pricing-faq-${kind})`} strokeWidth="4" strokeLinejoin="round" />
          <path d="M18 24l4 4 8-9" fill="none" stroke={`url(#pricing-faq-${kind})`} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
        </>}
      </svg>
    </span>
  );
}

function PricingCheck() {
  return <svg className="pricing-check" viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M4 10.5l3.4 3.4L16 5.8" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}

const pricingPlans = [
  {
    key: "free",
    name: "免费版",
    icon: "gift" as const,
    price: "¥0",
    suffix: "",
    monthlyNote: "永久免费",
    yearlyNote: "永久免费",
    description: "体验基础 AI 能力，快速生成 PPT",
    action: "立即体验",
    features: ["AI 生成次数 5 次/月", "基础模板使用", "导出格式：PPTX（含水印）", "云端存储空间 1GB", "高清导出速度", "社区支持"],
  },
  {
    key: "personal",
    name: "个人会员",
    icon: "user" as const,
    recommended: true,
    price: "¥29",
    suffix: "/月",
    monthlyNote: "¥ 29 / 月",
    yearlyNote: "¥ 348 / 年（省 ¥84）",
    description: "适合个人用户，畅享 AI 高效创作",
    action: "立即订阅",
    features: ["AI 生成次数 不限", "海量优质模板", "导出格式：PPTX / PDF / 图片", "云端存储空间 10GB", "高清导出 & 去水印", "优先生成通道", "专属客服支持"],
  },
  {
    key: "team",
    name: "团队会员",
    icon: "team" as const,
    price: "¥99",
    suffix: "/月 / 团队",
    monthlyNote: "¥ 99 / 月 / 团队",
    yearlyNote: "¥ 1,188 / 年（省 ¥288）",
    description: "适合小团队协作，提升团队效率",
    action: "立即订阅",
    features: ["AI 生成次数 不限", "团队模板库共享", "导出格式：PPTX / PDF / 图片", "云端存储空间 100GB", "团队协作与评审", "品牌规范与模板锁定", "成员管理（最多 20 人）", "优先生成通道", "专属客服支持"],
  },
  {
    key: "enterprise",
    name: "企业定制",
    icon: "building" as const,
    price: "定制报价",
    suffix: "",
    monthlyNote: "按需定制 专属方案",
    yearlyNote: "按需定制 专属方案",
    description: "适合中大型企业，安全合规可控",
    action: "联系销售",
    features: ["AI 生成次数 不限", "专属模板与品牌体系", "导出格式：PPTX / PDF / 图片", "专属云端存储（按需扩容）", "团队协作与权限管理", "企业级安全与合规", "API 接入与系统集成", "专属成功顾问与培训", "7×24 小时专属服务"],
  },
];

const pricingComparison = [
  { label: "AI 生成次数", values: ["5 次/月", "不限", "不限", "不限"] },
  { label: "模板使用", values: ["基础模板", "海量优质模板", "团队模板库共享", "专属模板与设计服务"] },
  { label: "导出格式", values: ["PPTX（含水印）", "PPTX / PDF / 图片", "PPTX / PDF / 图片", "PPTX / PDF / 图片"] },
  { label: "云端存储空间", values: ["1GB", "10GB", "100GB", "按需扩容"] },
  { label: "去水印", values: ["–", "check", "check", "check"] },
  { label: "团队协作与评论", values: ["–", "–", "check", "check"] },
  { label: "品牌规范与模板锁定", values: ["–", "–", "check", "check"] },
  { label: "成员管理", values: ["–", "–", "最多 20 人", "无限制"] },
  { label: "API 接入", values: ["–", "–", "–", "check"] },
  { label: "专属客服支持", values: ["社区支持", "专属客服", "专属客服", "专属顾问 7×24"] },
];

const pricingFaqs = [
  { icon: "refresh" as const, title: "会员可以随时取消吗？", description: "可以的，您可以随时在账户设置中取消订阅，取消后将在当前计费周期结束后失效。" },
  { icon: "document" as const, title: "是否支持开发票？", description: "支持，我们提供电子发票，可在订单完成后前往「发票中心」自助申请开票。" },
  { icon: "people" as const, title: "团队版如何开通？", description: "点击「团队会员」立即订阅，创建团队后可邀请成员加入，支持统一管理与权限分配。" },
  { icon: "shield" as const, title: "企业版支持私有化部署吗？", description: "支持，我们提供私有化部署方案，满足企业在数据安全与合规方面的更高要求。" },
];

export function LegalPage({ kind }: { kind: "privacy" | "terms" }) { return <SimpleMarketingPage eyebrow="法律与合规" title={kind==="privacy"?"隐私政策（内测占位）":"用户协议（内测占位）"} description="正式上线前将补充完整条款、数据删除、充值退款及第三方服务说明。"><div className="legal-copy"><h2>当前说明</h2><p>本页面用于产品结构与交互占位，不构成最终正式协议。内测期间请勿上传无权处理的敏感资料。</p></div></SimpleMarketingPage>; }

function SimpleMarketingPage({ eyebrow,title,description,children }:{eyebrow:string;title:string;description:string;children:ReactNode}) { return <div className="marketing-page"><MarketingHeader/><main><section className="subpage-hero"><span className="eyebrow">{eyebrow}</span><h1>{title}</h1><p>{description}</p></section><section className="section subpage-content">{children}</section></main><MarketingFooter/></div>; }

function MarketingFooter() { return <footer className="marketing-footer"><Brand/><p>让好内容更有说服力。</p><nav><Link to="/legal/privacy">隐私政策</Link><Link to="/legal/terms">用户协议</Link><Link to="/help">帮助</Link></nav><span>© 2026 幻视PPT</span></footer>; }

const generationMethods: GenerationMethod[] = [
  { title: "主题生成 PPT", description: "输入主题或重点，并上传资料生成完整 PPT", icon: IconMessageDots, accent: "#6724ff", preset: { source: "file" } },
  { title: "文档生成 PPT", description: "上传文档，自动提炼内容与演示结构", icon: IconFileDescription, accent: "#1977f3", preset: { source: "file" } },
  { title: "图片模式 PPT", description: "整页视觉生成，优先呈现力与画面质感", icon: IconPhoto, accent: "#ff7b0f", preset: { source: "file", generation: "image", theme: "business-blue" }, edition: "global-only" },
  { title: "文档总结 PPT", description: "长文档自动拆解，提炼大纲与重点", icon: IconFileText, accent: "#e63d73", preset: { source: "file", narrative: "pyramid" } },
  { title: "场景模板 PPT", description: "工作汇报、商业方案、培训课件快速套用", icon: IconMap, accent: "#8b58ff", preset: { source: "file", narrative: "briefing" } },
  { title: "美化已有 PPT", description: "上传旧文件，AI 重构版式与视觉风格", icon: IconPresentation, accent: "#3f61f4", preset: { source: "pptx" }, edition: "global-only" },
];

const HOME_STYLE_OPTIONS: Array<{ key: string; mode: "standard" | "image"; name: string }> = [
  ...Array.from({ length: 12 }, (_, index) => ({ key: `theme${String(index + 1).padStart(2, "0")}`, mode: "standard" as const, name: ["轻拟态风", "炫光紫绿风", "深浅代码风", "玻璃糖果风", "色谱图表风", "深色图谱风", "冷白调研风", "黑金实验风", "深蓝杂志风", "金色指数风", "高能增长风", "声波霓虹风"][index] })),
  { key: "business-blue", mode: "image", name: "商务蓝" },
  { key: "executive-navy", mode: "image", name: "高管深蓝" },
  { key: "minimal-light", mode: "image", name: "极简亮色" },
  { key: "data-editorial", mode: "image", name: "数据研报" },
  { key: "tech-dark", mode: "image", name: "深色科技" },
  { key: "warm-story", mode: "image", name: "暖调叙事" },
];

interface NewPptPreset {
  source?: "file" | "pptx";
  text?: string;
  title?: string;
  aiModel?: ChinaAiModel;
  audience?: string;
  audienceMode?: PptSelectionMode;
  scene?: string;
  sceneMode?: PptSelectionMode;
  customScene?: string;
  narrative?: string;
  generation?: "standard" | "image";
  theme?: string;
  pages?: string;
}

function formatFileSize(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function newPptPath(preset: NewPptPreset) {
  const params = new URLSearchParams();
  Object.entries(preset).forEach(([key, value]) => {
    if (typeof value === "string") params.set(key, value);
  });
  const query = params.toString();
  return `/app/new${query ? `?${query}` : ""}`;
}

const TEMPLATE_CATEGORIES = [
  { key: "all", label: "全部" },
  { key: "business", label: "商务" },
  { key: "tech", label: "科技" },
  { key: "education", label: "教育" },
  { key: "minimal", label: "简约" },
  { key: "creative", label: "创意" },
  { key: "marketing", label: "营销" },
  { key: "enterprise", label: "企业汇报" },
] as const;

const STYLE_POPULARITY = ["business-blue", "tech-dark", "minimal-light", "executive-navy", "data-editorial", "warm-story"];

type TemplateCategoryKey = typeof TEMPLATE_CATEGORIES[number]["key"];
